<template>
    <div class="modal fade" id="openReaderFormatModal" ref="modalForm" tabindex="-1" aria-labelledby="openReaderFormatModal" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <form v-if="selectedFormat" id="addGroupForm" class="modal-content">
                <div class="modal-header text-bg-primary">
                    <h5 class="modal-title" id="addGroupModalLabel">{{ $t('readerFormat.title') }}</h5>
                    <button type="button" class="btn-close text-light" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div class="mb-2">
                        <label class="form-label">{{ $t('readerFormat.type') }}</label>
                        <select class="form-select" v-model="selectedFormat.format_id">
                            <option v-for="type in availableReaderTypes" :key="type.formatId" :value="type.formatId">
                                {{ type.label }}
                            </option>
                        </select>
                    </div>
                    <div v-if="selectedType.hasSize" class="mb-2">
                        <label class="form-label">Nombre de {{ selectedType.dataType }}</label>
                        <input type="number" class="form-control form-control-sm" min="0" max="128" v-model="selectedFormat.size" />
                        <div v-if="sizeMessageError" ref="sizeError" class="mb-2 custom-error-msg">
                            {{ sizeMessageError }}
                        </div>
                    </div>
                    <div v-if="selectedType.hasCardNumber" class="mb-2">
                        <RangeComponent
                            ref="cardNumberRange"
                            :label="$t('componentEditReader.badgeNumber')"
                            v-model:range="selectedFormat.card_number"
                        />
                    </div>
                    <div v-if="selectedType.hasFacilityCode">
                        <div class="d-sm-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2 mb-2">
                            <label class="form-check-label flex-grow-1 mb-2 mb-sm-0 me-2" for="facility-code">{{ $t('readerFormat.facilityCode') }}</label>
                            <input type="checkbox" id="facility-code" v-model="selectedFormat.facility_code.enable" checked data-switch="bool">
                            <label for="facility-code" data-on-label="Oui" data-off-label="Non"></label>
                        </div>
                        <div v-if="selectedFormat.facility_code.enable" class="mb-2">
                            <RangeComponent
                                ref="facilityCodeRange"
                                v-model:range="selectedFormat.facility_code"
                            />
                        </div>
                    </div>
                    <div v-if="showFacilityCodes" class="mb-2">
                        <label for="reader-facility-codes" class="form-label">{{ $t('readerFormat.facilityCodes') }}</label>
                        <input
                            id="reader-facility-codes"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': facilityCodesMessageError }"
                            v-model.trim="facilityCodesInput"
                            inputmode="numeric"
                            :placeholder="$t('readerFormat.facilityCodesPlaceholder')"
                        />
                        <div v-if="facilityCodesMessageError" class="invalid-feedback">
                            {{ facilityCodesMessageError }}
                        </div>
                    </div>

                    <div v-if="selectedType.hasParities">
                        <div class="mb-2">
                            <div class="d-sm-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                <label class="form-check-label flex-grow-1 mb-2 mb-sm-0 me-2" for="parity1">{{ $t('readerFormat.parity1') }}</label>
                                <input type="checkbox" id="parity1" v-model="selectedFormat.parities[0].enable" checked data-switch="bool">
                                <label for="parity1" data-on-label="Oui" data-off-label="Non"></label>
                            </div>
                        </div>
                        <div v-if="selectedFormat.parities[0].enable" class="mb-2">
                            <ParityComponent
                                ref="firstParity"
                                v-model:parity="selectedFormat.parities[0]"
                            />
                        </div>
                        <div class="mb-2">
                            <div class="d-sm-flex justify-content-between align-items-center bg-primary-lighten rounded py-1 px-2">
                                <label class="form-check-label flex-grow-1 mb-2 mb-sm-0 me-2" for="parity2">{{ $t('readerFormat.parity2') }}</label>
                                <input type="checkbox" id="parity2" v-model="selectedFormat.parities[1].enable" checked data-switch="bool">
                                <label for="parity2" data-on-label="Oui" data-off-label="Non"></label>
                            </div>
                        </div>
                        <div v-if="selectedFormat.parities[1].enable" class="mb-2">
                            <ParityComponent
                                ref="secondParity"
                                v-model:parity="selectedFormat.parities[1]"
                            />
                        </div>
                    </div>

                    <div v-if="selectedType.hasIdentifierType" class="mb-2">
                        <label for="reader-identifier-type" class="form-label">{{ $t('readerFormat.identifierType') }}</label>
                        <select id="reader-identifier-type" class="form-select" v-model="selectedFormat.identifier_type">
                            <option v-for="option in identifierTypes" :key="option.value" :value="option.value">
                                {{ option.label }}
                            </option>
                        </select>
                    </div>
                    <div v-if="selectedType.hasIdentifierType && selectedFormat.identifier_type === 'code'" class="mb-2">
                        <label for="reader-code-length" class="form-label">{{ $t('readerFormat.keypadCodeLength') }}</label>
                        <input
                            id="reader-code-length"
                            type="number"
                            class="form-control"
                            min="1"
                            max="10"
                            step="1"
                            v-model.number="codeLength"
                        />
                    </div>

                    <div v-if="selectedType.hasKeypad" class="mb-2">
                        <h5 class="header-title mb-2">{{ $t('readerFormat.keypadSection') }}</h5>
                        <label for="reader-keypad-code-length" class="form-label">{{ $t('readerFormat.keypadCodeLength') }}</label>
                        <select id="reader-keypad-code-length" class="form-select" v-model="keypadCodeLength">
                            <option :value="0">{{ $t('readerFormat.keypadHashTerminates') }}</option>
                            <option v-for="n in 10" :key="n" :value="n">{{ n }}</option>
                        </select>
                    </div>

                    <div v-if="selectedType.is_rs485" class="mt-3">
                        <div class="mb-2">
                            <label for="reader-bus-address" class="form-label">{{ $t('readerFormat.busAddress') }}</label>
                            <input
                                id="reader-bus-address"
                                type="number"
                                class="form-control"
                                :class="{ 'is-invalid': busAddressMessageError }"
                                min="0"
                                max="255"
                                step="1"
                                v-model.number="selectedFormat.bus_address"
                                required
                            />
                            <div v-if="busAddressMessageError" class="invalid-feedback">
                                {{ busAddressMessageError }}
                            </div>
                        </div>
                        <div class="mb-2">
                            <label for="reader-tamper" class="form-label">{{ $t('readerFormat.tamper') }}</label>
                            <select id="reader-tamper" class="form-select" v-model="selectedFormat.tamper">
                                <option value="Inactive">{{ $t('readerFormat.tamperInactive') }}</option>
                                <option value="Active">{{ $t('readerFormat.tamperActive') }}</option>
                                <option value="ActiveWithKeyErasure">{{ $t('readerFormat.tamperActiveErase') }}</option>
                            </select>
                        </div>
                        <div class="form-check mb-2">
                            <input
                                id="use-default-key"
                                type="checkbox"
                                class="form-check-input"
                                v-model="selectedFormat.use_default_key"
                            />
                            <label class="form-check-label" for="use-default-key">{{ $t('readerFormat.useDefaultKey') }}</label>
                        </div>
                        <div v-if="!selectedFormat.use_default_key" class="mb-2">
                            <PasswordComponent
                                ref="communicationKeyInput"
                                id="reader-communication-key"
                                v-model="selectedFormat.communication_key"
                                :label="$t('readerFormat.communicationKey')"
                                autocomplete="new-password"
                                :hint-text="$t('readerFormat.communicationKeyHint')"
                            />
                            <div v-if="communicationKeyMessageError" class="custom-error-msg">
                                {{ communicationKeyMessageError }}
                            </div>
                        </div>
                        <div v-if="selectedType.has_ble" class="form-check mt-3 mb-2">
                            <input
                                id="show-stid-mobile-settings"
                                type="checkbox"
                                class="form-check-input"
                                v-model="selectedFormat.show_bluetooth_config"
                            />
                            <label class="form-check-label" for="show-stid-mobile-settings">
                                {{ $t('readerFormat.showStid') }}
                            </label>
                        </div>
                        <div v-if="selectedFormat.show_bluetooth_config" class="mt-3">
                            <div class="stid-mobile-divider mb-2">
                                <span>STid Mobile ID</span>
                            </div>
                            <h5 class="header-title mb-3">{{ $t('readerFormat.identModes') }}</h5>
                            <div class="row">
                                <template v-for="mode in bluetoothModeFields" :key="mode.key">
                                    <div class="mb-2 col-md-6">
                                        <label :for="`bluetooth-${mode.key}`" class="form-label">{{ mode.label }}</label>
                                        <select
                                            :id="`bluetooth-${mode.key}`"
                                            class="form-select"
                                            :disabled="isBluetoothModeDisabled(mode.key)"
                                            v-model="selectedFormat.bluetooth_config[mode.key]"
                                        >
                                            <option v-for="option in mode.options" :key="option.value" :value="option.value">
                                                {{ option.label }}
                                            </option>
                                        </select>
                                        <div v-if="mode.key === 'remote_mode'" class="d-flex gap-3 mt-2">
                                            <div class="form-check">
                                                <input
                                                    id="bluetooth-remote-id-0"
                                                    type="radio"
                                                    class="form-check-input"
                                                    name="bluetooth-remote-id"
                                                    :value="0"
                                                    :disabled="!isRemoteModeActive()"
                                                    v-model.number="selectedFormat.bluetooth_config.remote_id"
                                                />
                                                <label class="form-check-label" for="bluetooth-remote-id-0">Remote 1</label>
                                            </div>
                                            <div class="form-check">
                                                <input
                                                    id="bluetooth-remote-id-1"
                                                    type="radio"
                                                    class="form-check-input"
                                                    name="bluetooth-remote-id"
                                                    :value="1"
                                                    :disabled="!isRemoteModeActive()"
                                                    v-model.number="selectedFormat.bluetooth_config.remote_id"
                                                />
                                                <label class="form-check-label" for="bluetooth-remote-id-1">Remote 2</label>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>
                        </div>
                    </div>

                    <div class="mt-3 d-flex justify-content-center align-items-center">
                        <button type="button" class="btn btn-outline-secondary me-2" style="min-width: 140px;" ref="closeButton" data-bs-dismiss="modal">{{ $t('readerFormat.back') }}</button>
                        <button type="button" class="btn btn-primary" style="min-width: 140px;" @click="handleSubmit">{{ $t('readerFormat.save') }}</button>
                    </div>
                </div>
            </form>
        </div>
    </div>
</template>

<script>
import { useI18n } from 'vue-i18n';
import { ref, onMounted, computed, nextTick, watch } from 'vue';
import ReaderFormat from '@/models/readerFormat';
import RangeComponent from "@/components/range.vue";
import ParityComponent from "@/components/parity.vue";
import Parity from '@/models/parity.js';
import Range from '@/models/range.js';
import PasswordComponent from '@/components/password.vue';
import BluetoothConfig from '@/models/bluetoothConfig';

    export default {
        name: 'ReaderFormatModal',
        components: {
            RangeComponent,
            ParityComponent,
            PasswordComponent,
        },
        emits: ['onReaderFormatUpdated'],
        setup(props, {emit}) {
        const { t } = useI18n();
            const modalForm = ref(null);
            const closeButton = ref(null);
            const cardNumberRange = ref(null);
            const facilityCodeRange = ref(null);
            const firstParity = ref(null);
            const secondParity = ref(null);
            const communicationKeyInput = ref(null);
            const sizeMessageError = ref('');
            const facilityCodesMessageError = ref('');
            const busAddressMessageError = ref('');
            const communicationKeyMessageError = ref('');
            const facilityCodesInput = ref('');
            let formatIndex = 0;

            const selectedFormat = ref(null);
            const availableReaderTypes = ref(null);
            const usedBusAddresses = ref(new Set());
            const bluetoothModeFields = [
                {
                    key: 'card_mode',
                    label: t('readerFormat.bt.card'),
                    options: [
                        { value: 'Inactive', label: t('readerFormat.bt.disabled') },
                        { value: 'Nearest', label: t('readerFormat.bt.contact') },
                        { value: 'Near', label: t('readerFormat.bt.upTo', { d: '0,2m' }) },
                        { value: 'Long', label: t('readerFormat.bt.upTo', { d: '0,3m' }) },
                        { value: 'ReallyLong', label: t('readerFormat.bt.upTo', { d: '0,5m' }) },
                    ],
                },
                {
                    key: 'hand_free_mode',
                    label: t('readerFormat.bt.handFree'),
                    options: [
                        { value: 'Inactive', label: t('readerFormat.bt.disabled') },
                        { value: 'Nearest', label: t('readerFormat.bt.upTo', { d: '3m' }) },
                        { value: 'Near', label: t('readerFormat.bt.upTo', { d: '5m' }) },
                        { value: 'Long', label: t('readerFormat.bt.upTo', { d: '10m' }) },
                    ],
                },
                {
                    key: 'slide_mode',
                    label: t('readerFormat.bt.slide'),
                    options: [
                        { value: 'Inactive', label: t('readerFormat.bt.disabled') },
                        { value: 'Nearest', label: t('readerFormat.bt.veryNear') },
                        { value: 'Near', label: t('readerFormat.bt.near') },
                        { value: 'Long', label: t('readerFormat.bt.medium') },
                        { value: 'ReallyLong', label: t('readerFormat.bt.far') },
                        { value: 'ExtraLong', label: t('readerFormat.bt.veryFar') },
                    ],
                },
                {
                    key: 'tap_mode',
                    label: t('readerFormat.bt.tapTap'),
                    options: [
                        { value: 'Inactive', label: t('readerFormat.bt.disabled') },
                        { value: 'Nearest', label: t('readerFormat.bt.upTo', { d: '3m' }) },
                        { value: 'Near', label: t('readerFormat.bt.upTo', { d: '5m' }) },
                        { value: 'Long', label: t('readerFormat.bt.upTo', { d: '10m' }) },
                        { value: 'ReallyLong', label: t('readerFormat.bt.upTo', { d: '15m' }) },
                    ],
                },
                {
                    key: 'remote_mode',
                    label: t('readerFormat.bt.remote'),
                    options: [
                        { value: 'Inactive', label: t('readerFormat.bt.disabled') },
                        { value: 'Nearest', label: t('readerFormat.bt.upTo', { d: '3m' }) },
                        { value: 'Near', label: t('readerFormat.bt.upTo', { d: '10m' }) },
                        { value: 'Long', label: t('readerFormat.bt.upTo', { d: '15m' }) },
                        { value: 'ReallyLong', label: t('readerFormat.bt.upTo', { d: '20m' }) },
                    ],
                },
            ];

            const isHandFreeModeActive = () => {
                return selectedFormat.value?.bluetooth_config?.hand_free_mode !== 'Inactive';
            };

            const isRemoteModeActive = () => {
                return selectedFormat.value?.bluetooth_config?.remote_mode !== 'Inactive';
            };

            const forceHandFreeIncompatibleModes = () => {
                if(!isHandFreeModeActive()) return;
                selectedFormat.value.bluetooth_config.slide_mode = 'Inactive';
                selectedFormat.value.bluetooth_config.tap_mode = 'Inactive';
                selectedFormat.value.bluetooth_config.remote_mode = 'Inactive';
            };

            const isBluetoothModeDisabled = (key) => {
                return isHandFreeModeActive() && ['slide_mode', 'tap_mode', 'remote_mode'].includes(key);
            };

            const setFormat = (index, format, availableTypes, busAddresses = []) => {
                formatIndex = index;
                availableReaderTypes.value = availableTypes;
                usedBusAddresses.value = new Set(busAddresses.map(Number));
                if(index == -1) {
                    selectedFormat.value = new ReaderFormat();
                    selectedFormat.value.format_id = availableTypes[0].formatId;
                } else {
                    selectedFormat.value = ReaderFormat.constructFromObject(format);
                }
                selectedFormat.value.bluetooth_config = BluetoothConfig.constructFromObject(selectedFormat.value.bluetooth_config);
                facilityCodesInput.value = selectedFormat.value.facility_codes.join(',');
                forceHandFreeIncompatibleModes();
                nextTick(() => {
                    communicationKeyInput.value?.setVisible(false);
                });
            };

            const generateCommunicationKey = () => {
                const bytes = new Uint8Array(16);
                window.crypto.getRandomValues(bytes);
                selectedFormat.value.communication_key = Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('').toUpperCase();
            };

            const selectedType = computed(() => {
                return availableReaderTypes.value.find(type => type.formatId === selectedFormat.value.format_id);
            });

            const identifierTypes = [
                { value: 'unknown', label: t('readerFormat.identifierTypeAll') },
                { value: 'code', label: t('readerFormat.identifierTypeCodes') },
                { value: 'qrcode', label: t('readerFormat.identifierTypeQrCodes') },
            ];

            // Number of characters of the keypad codes read (1 to 10)
            const codeLength = computed({
                get: () => selectedFormat.value?.keypad_code_length ?? 4,
                set: (value) => {
                    const length = Number(value);
                    selectedFormat.value.keypad_code_length = Number.isInteger(length) ? Math.min(Math.max(length, 1), 10) : 4;
                },
            });

            // 0 (absent) : the keypad entry is completed automatically by '#'
            const keypadCodeLength = computed({
                get: () => selectedFormat.value?.keypad_code_length ?? 0,
                set: (value) => {
                    selectedFormat.value.keypad_code_length = value > 0 ? value : null;
                },
            });

            const showFacilityCodes = computed(() => {
                const type = selectedType.value;
                return Boolean(
                    type?.config?.facility_code
                    || (type?.hasFacilityCode && selectedFormat.value.facility_code.enable)
                );
            });

            const resetForm = () => {
                sizeMessageError.value = '';
                facilityCodesMessageError.value = '';
                busAddressMessageError.value = '';
                communicationKeyMessageError.value = '';
            };

            const validateFacilityCodes = () => {
                const input = facilityCodesInput.value.replaceAll(' ', '');
                if(!input) {
                    facilityCodesMessageError.value = 'Veuillez saisir au moins un code site.';
                    return false;
                }

                const codes = [];
                for(const part of input.split(',')) {
                    if(!part) {
                        facilityCodesMessageError.value = t('readerFormat.errors.checkInput');
                        return false;
                    }
                    if(!/^\d+$/.test(part)) {
                        facilityCodesMessageError.value = `Valeur invalide : "${part}".`;
                        return false;
                    }

                    const code = Number(part);
                    if(code < 1 || code > 65535) {
                        facilityCodesMessageError.value = t('readerFormat.errors.facilityCodeRange');
                        return false;
                    }
                    codes.push(code);
                }

                selectedFormat.value.facility_codes = codes;
                return true;
            };

            const handleSubmit = () => {
                let hasErrors = false;
                resetForm();

                if (selectedFormat.value.size < selectedType.value.minSize || selectedFormat.value.size > selectedType.value.maxSize) {
                    sizeMessageError.value = t('readerFormat.errors.sizeBetween', { min: selectedType.value.minSize, max: selectedType.value.maxSize, unit: selectedType.value.dataType });
                }

                if(selectedType.value.hasCardNumber) {
                    const sizeError = t('readerFormat.errors.badgeSize', { min: selectedType.value.cardNumberMinSize, max: selectedType.value.cardNumberMaxSize, unit: selectedType.value.dataType });
                    hasErrors |= !cardNumberRange.value.validate(selectedFormat.value.size, selectedType.value.cardNumberMinSize, selectedType.value.cardNumberMaxSize, sizeError);
                }

                if(selectedType.value.hasFacilityCode && selectedFormat.value.facility_code.enable) {
                    const sizeError = t('readerFormat.errors.facilityCodeSize', { min: selectedType.value.facilityCodeMinSize, max: selectedType.value.facilityCodeMaxSize, unit: selectedType.value.dataType });
                    hasErrors |= !facilityCodeRange.value.validate(selectedFormat.value.size, selectedType.value.facilityCodeMinSize, selectedType.value.facilityCodeMaxSize, sizeError);
                }

                if(showFacilityCodes.value) {
                    hasErrors |= !validateFacilityCodes();
                } else {
                    selectedFormat.value.facility_codes = [];
                }

                if(selectedType.value.hasParities) {
                    if(selectedFormat.value.parities[0].enable) {
                        hasErrors |= !firstParity.value.validate(selectedFormat.value.size);
                    }
                    if(selectedFormat.value.parities[1].enable) {
                        hasErrors |= !secondParity.value.validate(selectedFormat.value.size);
                    }
                }

                if(selectedType.value.is_rs485) {
                    const busAddress = Number(selectedFormat.value.bus_address);
                    if(!Number.isInteger(busAddress) || busAddress < 0 || busAddress > 255) {
                        busAddressMessageError.value = t('readerFormat.errors.busAddressRange');
                        hasErrors = true;
                    } else if(usedBusAddresses.value.has(busAddress)) {
                        busAddressMessageError.value = t('readerFormat.errors.busAddressUsed', { address: busAddress });
                        hasErrors = true;
                    }

                    if(!selectedFormat.value.use_default_key) {
                        const communicationKey = selectedFormat.value.communication_key || '';

                        if(communicationKey.length !== 32) {
                            communicationKeyMessageError.value = t('readerFormat.errors.keyLength');
                            hasErrors = true;
                        } else if(!/^[0-9a-fA-F]{32}$/.test(communicationKey)) {
                            communicationKeyMessageError.value = t('readerFormat.errors.keyHex');
                            hasErrors = true;
                        }
                    }
                }

                if(hasErrors == 0) {
                    const { config, formatType: format_type, encoding } = selectedType.value;
                    if(!selectedType.value.hasIdentifierType) {
                        // Type imposed by the reader type (e.g. a keypad-only reader reads codes)
                        selectedFormat.value.identifier_type = selectedType.value.identifierType ?? 'unknown';
                    }
                    if(selectedFormat.value.identifier_type !== 'code' && !selectedType.value.hasKeypad) {
                        selectedFormat.value.keypad_code_length = null;
                    }
                    Object.assign(selectedFormat.value, {
                        format_type,
                        encoding: encoding || 'Hex',
                        ...(config
                            ? {
                                size: config.size ?? selectedFormat.value.size,
                                card_number: Range.constructFromObject(config.card_number),
                                facility_code: Range.constructFromObject(config.facility_code),
                                parities: Parity.fromConfig(config),
                                haxo_scan: config.haxo_scan ?? false,
                            }
                            : {
                                ...{card_number: new Range(selectedType.value.hasCardNumber, selectedFormat.value.card_number.start, selectedFormat.value.card_number.end)},
                                ...(selectedType.value.hasFacilityCode ? {} : { facility_code: new Range() }),
                                ...(selectedType.value.hasParities ? {} : { parities: [new Parity(), new Parity()] }),
                                haxo_scan: false,
                            })
                    });
                    emit('onReaderFormatUpdated', formatIndex, selectedFormat.value);
                    closeButton.value.click();
                }
            };

            onMounted(() => {
                modalForm.value.addEventListener('hidden.bs.modal', resetForm);
            });

            watch(
                () => selectedFormat.value?.bluetooth_config?.hand_free_mode,
                forceHandFreeIncompatibleModes
            );

            watch(
                () => selectedFormat.value?.identifier_type,
                (identifierType) => {
                    if(identifierType === 'code' && selectedType.value?.hasIdentifierType) {
                        selectedFormat.value.keypad_code_length ??= 4;
                    }
                }
            );

            return {
                modalForm,
                selectedFormat,
                selectedType,
                closeButton,
                cardNumberRange,
                facilityCodeRange,
                firstParity,
                secondParity,
                communicationKeyInput,
                sizeMessageError,
                facilityCodesMessageError,
                busAddressMessageError,
                communicationKeyMessageError,
                facilityCodesInput,
                identifierTypes,
                codeLength,
                keypadCodeLength,
                showFacilityCodes,
                bluetoothModeFields,
                isBluetoothModeDisabled,
                isRemoteModeActive,
                generateCommunicationKey,
                handleSubmit,
                setFormat,
                availableReaderTypes,
            };
        },
    };
</script>

<style scoped>
.custom-error-msg {
  color: red;
  font-size: 14px;
}

.stid-mobile-divider {
  align-items: center;
  color: #6c757d;
  display: flex;
  font-size: 0.82rem;
  font-weight: 600;
  gap: 0.75rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.stid-mobile-divider::before,
.stid-mobile-divider::after {
  background-color: #dee2e6;
  content: "";
  flex: 1;
  height: 1px;
}
</style>
